<template>
    <wrapper-features-templates-operations
    :hideWhiteBoxOnLoad="showLoader || (!showLoader && showAccountsEmptyState && !applyErrorState)"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showStepper="!showLoader && activeStep !== 3 && !showAccountsEmptyState && !applyErrorState"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :showFooter="!showLoader && activeStep !== 3 && !showAccountsEmptyState && !applyErrorState"
    :footerButtonsParams="footerButtonsParams"
    :scrollValue="scrollValue"
    :feedbackOperationData="feedbackOperationData"
    :tokenTitleAppend="!showCreateBeneficiaryModal ? t('transfers.nacional.tokenTitle') : t('transfers.nacional.beneficiaryTokenTitle')"
    @challengeCancelHandler="challengeCancelHandler"
    @challengeSuccessHandler="challengeSuccessFn($event)"
    @challengeErrorHandler="challengeErrorFn($event)"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Content -->
        <component
        v-if="!showLoader && !applyErrorState && !showAccountsEmptyState"
        :is="activeStepComponent"
        :rtgsResponse="rtgsResponse"
        :formData="formData"
        :formTabsList="formTabsList"
        :frequencyTabsList="frequencyTabsList"
        :showInputsErrors="showInputsErrors"
        :beneficiaryInfo="searchedBeneficiaryInfo"
        :beneficiaryInfoError="beneficiaryInfoError"
        :validatedBeneficiary="validatedBeneficiary"
        :clearForm="clearForm"
        :responseData="responseData"
        :isFavorite="isFavorite"
        :beneficiaryAdded="beneficiaryAdded"
        @updateFormData="updateFormDataHandler"
        @updateSelectedTab="tabsSegmentClickHandler($event)"
        @updateFrequencySelectedTab="frequencyTabsClickHandler($event)"
        @requestBeneficiaryInfo="requestBeneficiaryInfoHandler"
        @requestBeneficiaryName="validateBeneficiaryName"
        @resetBeneficiaryInfoError="resetBeneficiaryInfoErrorHandler"
        @changeBeneficiary="showContactsModal = true"
        @openQrCodeModal="showQrCodeModal = true"
        @requestMotivesList="requestMotivesListHandler"
        @resetShowInputsErrors="showInputsErrors = false"
        @formIsCleared="clearForm = false"
        @scrollTo="scrollValue = $event"
        @openReuseTransferModal="showReuseTransferModal = true"
        @openReuseBeneficiaryModal="showReuseBeneficiaryModal = true"
        @showFavoriteModal="showFavoritesModal = true"
        @showCreateBeneficiaryModal="showCreateBeneficiaryModal = true"
        @newTransfer="newTransferHandler"/>
        <template #empty-state-slot>
            <!-- Empty State -->
            <empty-state-generic
            v-if="showAccountsEmptyState && !applyErrorState"
            illustration="definitions-accounts-groups"
            :title="t('transfers.nacional.noAccountsTitle')"
            :description="t('transfers.nacional.noAccountsText')"/>
        </template>
    </wrapper-features-templates-operations>
    <!-- Reuse Transfer Modal -->
    <reuse-transfer-modal
    v-if="showReuseTransferModal"
    :transferType="formTabsList.find(tab => tab.selected === true).id"
    @hideMe="showReuseTransferModal = false"
    @reuseTransferRowClicked="setDataFromReuseModal"/>
    <!-- Reuse Beneficiary Modal -->
    <reuse-beneficiary-modal
    v-if="showReuseBeneficiaryModal"
    :transferType="formTabsList.find(tab => tab.selected === true).id"
    @hideMe="showReuseBeneficiaryModal = false"
    @reuseBeneficiaryRowClicked="setDataFromReuseBeneficiary"/>
    <!-- Favorites modal -->
    <favorites-modal
    v-if="showFavoritesModal"
    rightButtonIcon="check-line"
    :modalParams="{ operationId: responseData.operationId, type: isFavorite ? 'remove' : 'create' }"
    @hideMe="showFavoritesModal = false"
    @requestSuccessful="favoriteModalSuccessHandler($event)"
    @requestError="favoriteModalErrorHandler($event)"/>
    <!-- Create Beneficiary Modal -->
    <create-beneficiary-modal
    v-if="showCreateBeneficiaryModal"
    :formData="formData"
    :formTabsList="formTabsList"
    :preventClickOutsideClose="preventBeneficiaryModalClose"
    @createBeneficiary="createBeneficiaryRequest($event)"
    @hideMe="showCreateBeneficiaryModal = false"/>
    <!-- Contacts Modal -->
    <modal-side-contacts
    v-if="showContactsModal"
    icon="close"
    sendType="sms"
    :triggerClose="triggerContactsModalClose"
    :title="t('transfers.nacional.contactsModal.title')"
    :subTitle="t('transfers.nacional.contactsModal.subTitle')"
    :destinationValues="[formData.genericDestination]"
    :showOnlyBAIContacts="true"
    @contactSelected="mobileContactSelectedHandler($event)"
    @hideMe="[showContactsModal = false, triggerContactsModalClose = false]"/>
    <!-- QR Code Modal -->
    <modal-side-qr-code-reader
    v-if="showQrCodeModal"
    :title="t('transfers.nacional.step1.tabs.naHora')"
    @qrCodeComplete="qrCodeSuccessHandler"
    @hideMe="[showQrCodeModal = false, destroyQrCodeHandler()]"/>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import CreateBeneficiaryModal from '@/components/features/transfers/nacional/CreateBeneficiaryModal'
import ReuseTransferModal from '@/components/features/transfers/nacional/ReuseTransferModal'
import ReuseBeneficiaryModal from '@/components/features/transfers/nacional/ReuseBeneficiaryModal'
import ModalSideContacts from '@/components/generic/modals/side/contacts/Base'
import ModalSideQrCodeReader from '@/components/generic/modals/side/QrCodeReader'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount, onMounted } from 'vue'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* Aux */
import { clearObjectStrings, atLeastOneIsEmpty } from '@/assets/scripts/objects'

/* Api */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const {
    requestTransfersRTGS,
    requestTansfersCategoriesList,
    requestAccountsBalances,
    requestBeneficiaryDestinationInfo,
    requestBeneficiaryName,
    requestTransfersScheduleTypes,
    requestTransfersMotivesList,
    createPontualTransfer,
    createRecurringTransfer,
    createSmartTransfer,
    createBeneficiary,
    clearFeatureStates
} = mapActions(moduleMapper.TRANSFERS_NACIONAL)

const {
    getAccountsBalances,
    getScheduleTypes
} = mapGetters(moduleMapper.TRANSFERS_NACIONAL)

const { getAccountGroups } = mapGetters(moduleMapper.SESSION)

/** Props
@favoriteInfo {Object} - Object with favorite info
@transferSelectedTabId {String} - id of transfer selected on render
@params { Object } - data received from the chosing to reused frequent transfer
@dashboardCardInfo {Object} - info received to select right tab when entering view
@accountId {Number} - prop to selected account
*/
const props = defineProps({
    favoriteInfo: Object,
    transferSelectedTabId: String,
    params: Object,
    dashboardCardInfo: Object
})

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showReuseTransferModal = ref(false)
const showReuseBeneficiaryModal = ref(false)
const showAccountsEmptyState = computed(() => {	return !showLoader.value && !getAccountsBalances.value.length })
const isCompanySession = computed(() => {
    return getAccountGroups.value.find(obj => obj.default).type.toLowerCase() === 'company'
})
const showFavoritesModal = ref(false)
const showCreateBeneficiaryModal = ref(false)
const preventBeneficiaryModalClose = ref(false)
const showContactsModal = ref(false)
const triggerContactsModalClose = ref(false)
const showQrCodeModal = ref(false)
// We use this to prevent slow computers to spam click and call mutiply transfers requests before loader is active
const transferRequestHappening = ref(false)

/*************************************************
*                                                *
*                  Steps Logic                   *
*                                                *
*************************************************/
const activeStep = ref(1)

const stepperParams = ref([
    { description: t('transfers.nacional.step1.title') },
    { description: t('transfers.nacional.step2.title') }
])

const activeStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/transfers/nacional/Step' + stepNumber))
})

const changeStepHandler = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const newTransferHandler = () => {
    formTabsList.value.forEach(tab => {
        tab.selected = tab.id === false
    })
    formTabsList.value[0].selected = true

    changeStepHandler(1)
    clearAllFields()
    initialRequests()
}

/*************************************************
*                                                *
*                   Form Data                    *
*                                                *
*************************************************/
const scrollValue = ref(null)
const clearForm = ref(false)
const showInputsErrors = ref(false)
const validatedBeneficiary = ref(false)
const rtgsResponse = ref(null)
const searchedBeneficiaryInfo = ref(null)
const beneficiaryInfoError = ref({
    errorMessage: null,
    errorState: false
})

const formData = ref({
    /* Account Data */
    originAccount: '',
    accountDisplayInfo: '',
    accountCurrency: '',
    /* Destination Data */
    genericDestination: '',
    sendToSameHolder: false,
    beneficiaryName: '',
    address: '',
    /* Amount Data */
    amount: '',
    /* Frequency Data */
    urgentTransfer: false,
    startDate: new Date(),
    frequencyCode: '',
    frequency: '',
    endDate: '',
    endDateSelected: true,
    /* Operation */
    operationDescription: '',
    senderDescription: '',
    receiverDescription: '',
    category: '',
    motive: '',
    /* Notify Beneficiary */
    notifyBeneficiary: false,
    beneficiaryPhoneNumber: '',
    termsAccepted: false
})

const samebankOneOffTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
     /* Destination Data */
    'genericDestination',
    'beneficiaryName',
    /* Amount Data */
    'amount'
])

const samebankSendToSameHolderOneOffTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
     /* Destination Data */
    'genericDestination',
    /* Amount Data */
    'amount'
])

const samebankRecurringTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
    /* Destination Data */
    'genericDestination',
    'beneficiaryName',
    /* Frequency Data */
    'startDate',
    'frequencyCode',
    'endDate',
    /* Amount Data */
    'amount'
])

const samebankSendToSameHolderRecurringTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
    /* Destination Data */
    'genericDestination',
    /* Frequency Data */
    'startDate',
    'frequencyCode',
    'endDate',
    /* Amount Data */
    'amount'
])

const otherbankOneOffTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
     /* Destination Data */
    'genericDestination',
    'beneficiaryName',
    'address',
    /* Amount Data */
    'amount',
    /* Operation */
    'senderDescription',
    'category',
    'motive',
    /* Notify Beneficiary */
    'termsAccepted'
])

const otherbankRecurringTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
    /* Destination Data */
    'genericDestination',
    'beneficiaryName',
    'address',
    /* Frequency Data */
    'startDate',
    'frequencyCode',
    'endDate',
    /* Amount Data */
    'amount',
    /* Operation */
    'senderDescription',
    'category',
    'motive',
    /* Notify Beneficiary */
    'termsAccepted'
])

const naHoraTransferMandatoryList = ref([
    /* Account Data */
    'originAccount',
     /* Destination Data */
    'genericDestination',
    /* Amount Data */
    'amount'
])

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue

    if (field === 'genericDestination' && formTabsList.value[2].selected) {
        validatedBeneficiary.value = false
        searchedBeneficiaryInfo.value = null
    }

    if (field === 'sendToSameHolder' && inputValue) requestBeneficiaryInfoHandler()

    if (field === 'genericDestination' && formData.value.sendToSameHolder) requestBeneficiaryInfoHandler()
}

/*************************************************
*                                                *
*                   Tabs Data                    *
*                                                *
*************************************************/
const formTabsList = ref([
    {
        id: 'samebank',
        label: t('transfers.nacional.step1.tabs.samebank'),
        selected: props.transferSelectedTabId ? props.transferSelectedTabId === 'samebank' : true
    },
    {
        id: 'otherbank',
        label: t('transfers.nacional.step1.tabs.otherbank'),
        selected: props.transferSelectedTabId ? props.transferSelectedTabId === 'otherbank' : false
    },
    {
        id: 'naHora',
        label: t('transfers.nacional.step1.tabs.naHora'),
        selected: props.transferSelectedTabId ? props.transferSelectedTabId === 'naHora' : false
    }
])

const tabsSegmentClickHandler = event => {
    formTabsList.value.forEach(tab => {
        tab.selected = tab.id === event
    })

    clearAllFields()
}

const frequencyTabsList = ref([
    {
        id: 'oneOffTransfer',
        label: t('transfers.nacional.step1.frequencyTabs.oneOffTransfer'),
        selected: true
    },
    {
        id: 'recurringTransfer',
        label: t('transfers.nacional.step1.frequencyTabs.recurringTransfer'),
        selected: false
    }
])

const frequencyTabsClickHandler = event => {
    frequencyTabsList.value.forEach(tab => {
        tab.selected = tab.id === event
    })

    if (event === 'recurringTransfer') requestScheduleTypes()

    formData.value.startDate = new Date()
    formData.value.frequencyCode = ''
    formData.value.frequency = ''
    formData.value.endDate = ''
    formData.value.endDateSelected = true
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const validateInputs = () => {
    let mandatoryListToUse = null
    let errorValidators = null

    const transferType = formTabsList.value.find(tab => tab.selected === true).id
    const transferFrequency = frequencyTabsList.value.find(tab => tab.selected === true).id

    if (transferType === 'samebank') {
        const invalidFrequency = formData.value.frequencyCode === 'VARIAVEL' && parseInt(formData.value.frequency) === 0

        if (transferFrequency === 'oneOffTransfer') {
            mandatoryListToUse = formData.value.sendToSameHolder ? samebankSendToSameHolderOneOffTransferMandatoryList.value : samebankOneOffTransferMandatoryList.value
        } else {
            mandatoryListToUse = formData.value.sendToSameHolder ? samebankSendToSameHolderRecurringTransferMandatoryList.value : samebankRecurringTransferMandatoryList.value
        }

        formData.value.frequencyCode === 'VARIAVEL' ? mandatoryListToUse.push('frequency') : mandatoryListToUse = mandatoryListToUse.filter(field => field !== 'frequency')
        if (!formData.value.endDateSelected) mandatoryListToUse = mandatoryListToUse.filter(field => field !== 'endDate')

        errorValidators = (!validatedBeneficiary.value && !formData.value.sendToSameHolder) || Number(formData.value.amount) === 0 || invalidFrequency
    } else if (transferType === 'otherbank') {
        transferFrequency === 'oneOffTransfer' ? mandatoryListToUse = otherbankOneOffTransferMandatoryList.value : mandatoryListToUse = otherbankRecurringTransferMandatoryList.value

        formData.value.frequencyCode === 'VARIAVEL' ? mandatoryListToUse.push('frequency') : mandatoryListToUse = mandatoryListToUse.filter(field => field !== 'frequency')

        if (!formData.value.endDateSelected) mandatoryListToUse = mandatoryListToUse.filter(field => field !== 'endDate')

        const sameBankDestination = formData.value.genericDestination.includes('AO060040')

        errorValidators = Number(formData.value.amount) === 0 || !formData.value.termsAccepted || formData.value.beneficiaryName.length < 5 || sameBankDestination

        if (formData.value.urgentTransfer) {
            mandatoryListToUse = mandatoryListToUse.filter(field => field !== 'termsAccepted')

            errorValidators = Number(formData.value.amount) === 0 || formData.value.beneficiaryName.length < 5 || sameBankDestination
         }

        /* TODO PARA A FASE 2
        || (formData.value.notifyBeneficiary && !formData.value.beneficiaryPhoneNumber)
        */
    } else {
        errorValidators = Number(formData.value.amount) === 0
        mandatoryListToUse = naHoraTransferMandatoryList.value
    }

    /* TODO PARA A FASE 2
    || (formData.value.notifyBeneficiary && !formData.value.beneficiaryPhoneNumber)
    */
   const inputErrorValidation = errorValidators || atLeastOneIsEmpty(formData.value, mandatoryListToUse)

   if (inputErrorValidation) {
       showInputsErrors.value = true
    } else changeStepHandler(2)
}

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
    return [{
        label: t('transfers.nacional.footerButtons.clear'),
        buttonPosition: 'left',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { clearAllFields() }
    }, {
        label: t('transfers.nacional.footerButtons.forward'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { validateInputs() }
    }, {
        label: t('transfers.nacional.footerButtons.edit'),
        icon: 'arrow-left-full',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { changeStepHandler(1) }
    }, {
        label: t('transfers.nacional.footerButtons.validate'),
        icon: 'check-line',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { requestSelectedTransfer() }
    }]
})

const footerButtonsParams = computed(() => {
    return footerButtonsData.value.filter(ele => !ele.hidden)
})

const clearAllFields = () => {
    formData.value = clearObjectStrings(formData.value)

    formData.value = {
        ...formData.value,
        startDate: new Date(),
        originAccount: getAccountsBalances.value.find(account => account.default).id,
        accountDisplayInfo: getAccountsBalances.value.find(account => account.default).accountNumber,
        accountCurrency: getAccountsBalances.value.find(account => account.default).currency,
        sendToSameHolder: false,
        notifyBeneficiary: false,
        termsAccepted: false,
        endDateSelected: true,
        urgentTransfer: false
    }

    beneficiaryInfoError.value = {
        errorMessage: null,
        errorState: false
    }

    frequencyTabsClickHandler('oneOffTransfer')

    searchedBeneficiaryInfo.value = null
    validatedBeneficiary.value = false
    beneficiaryAdded.value = false
    showInputsErrors.value = false
    clearForm.value = true
    scrollValue.value = 0
    responseData.value = null
    feedbackOperationData.value = null
}

/*************************************************
*                                                *
*               Reuse Transfer Logic             *
*                                                *
*************************************************/
const getParameterValue = (paramsList, paramName) => {
    const targetParam = paramsList.find(param => param.name === paramName)

    return targetParam && targetParam.value ? targetParam.value : ''
}

const setDataFromFavoritePropsParams = params => {
    const favoriteInfo = params.customerOperationView
    if (favoriteInfo.operationType.id === 74) {
        tabsSegmentClickHandler('samebank')
    } else if (favoriteInfo.operationType.id === 75) {
        tabsSegmentClickHandler('otherbank')
    } else {
        tabsSegmentClickHandler('naHora')
    }

    const transferType = formTabsList.value.find(tab => tab.selected === true).id

    Object.keys(formData.value).forEach(key => {
        if (key in params) formData.value[key] = params[key]
    })

    /* Amount Data */
    formData.value.amount = Number(getParameterValue(favoriteInfo.parameters, 'amount'))
    if (transferType !== 'naHora') {
        /* Destination Data */
        formData.value.genericDestination = getParameterValue(favoriteInfo.parameters, 'genericDestination')

        /* Operation */
        formData.value.senderDescription = getParameterValue(favoriteInfo.parameters, 'description')
        formData.value.operationDescription = getParameterValue(favoriteInfo.parameters, 'descriptionDestination')
    } else {
        /* Destination Data */
        formData.value.genericDestination = getParameterValue(favoriteInfo.parameters, 'destinationMsisdn')
        /* Operation */
        formData.value.operationDescription = getParameterValue(favoriteInfo.parameters, 'description')
    }

    const accountNumber = !transferType === 'naHora' ? getParameterValue(favoriteInfo.parameters, 'originAccountNumber') : getParameterValue(favoriteInfo.parameters, 'originAccountId')

    /* If account still exists in our account list */
    if (getAccountsBalances.value.find(account => account.accountNumber === accountNumber)) {
        formData.value.originAccount = getAccountsBalances.value.find(account => account.accountNumber === accountNumber).id
        /* Setting Initial Account Display Info from Default Account */
        formData.value.accountDisplayInfo = accountNumber
        /* Setting Initial Currency from Default Account */
        formData.value.accountCurrency = getAccountsBalances.value.find(account => account.accountNumber === accountNumber).currency
    }

    if (transferType === 'otherbank') {
        formData.value.beneficiaryName = getParameterValue(favoriteInfo.parameters, 'beneficiaryName')
        formData.value.address = getParameterValue(favoriteInfo.parameters, 'addressLine1')
        formData.value.category = getParameterValue(favoriteInfo.parameters, 'category')
        formData.value.motive = getParameterValue(favoriteInfo.parameters, 'motive')
        requestMotivesListHandler()
    } else if (transferType === 'samebank') {
        requestBeneficiaryInfoHandler()
    } else validateBeneficiaryName()
}

const setDataFromReusePropsParams = params => {
    if (params.operationId === 74) {
        tabsSegmentClickHandler('samebank')
    } else if (params.operationId === 75) {
        tabsSegmentClickHandler('otherbank')
    } else {
        tabsSegmentClickHandler('naHora')
        reuseNaHoraFromPropsParams(params)
        return
    }

    const transferType = formTabsList.value.find(tab => tab.selected === true).id

    Object.keys(formData.value).forEach(key => {
        if (key in params) formData.value[key] = params[key]
    })

    /* Destination Data */
    formData.value.genericDestination = params.destinationAccountNumber
    /* Amount Data */
    formData.value.amount = params.amount.amount
    /* Operation */
    formData.value.senderDescription = params.description
    formData.value.receiverDescription = params.descriptionDestination

    /* If account still exists in our account list */
    if (getAccountsBalances.value.find(account => account.accountNumber === params.originAccountNumber)) {
        formData.value.originAccount = getAccountsBalances.value.find(account => account.accountNumber === params.originAccountNumber).id
        /* Setting Initial Account Display Info from Default Account */
        formData.value.accountDisplayInfo = params.originAccountNumber
        /* Setting Initial Currency from Default Account */
        formData.value.accountCurrency = getAccountsBalances.value.find(account => account.accountNumber === params.originAccountNumber).currency
    }

    if (transferType === 'otherbank') {
        formData.value.beneficiaryName = params.beneficiaryName
        formData.value.category = params.category
        formData.value.motive = params.motive
        requestMotivesListHandler()
    } else if (transferType === 'samebank') {
        requestBeneficiaryInfoHandler()
    } else validateBeneficiaryName()
}

const reuseNaHoraFromPropsParams = params => {
    params.lineGroups.forEach(lineGroup => {
        lineGroup.lines.forEach(line => {
            if (line.fieldName === 'originAccount') {
                const accountNumber = line.lineValue
                /* If account still exists in our account list */
                if (getAccountsBalances.value.find(account => account.accountNumber === accountNumber)) {
                    formData.value.originAccount = getAccountsBalances.value.find(account => account.accountNumber === accountNumber).id
                    /* Setting Initial Account Display Info from Default Account */
                    formData.value.accountDisplayInfo = accountNumber
                    /* Setting Initial Currency from Default Account */
                    formData.value.accountCurrency = getAccountsBalances.value.find(account => account.accountNumber === accountNumber).currency
                }
            }
            if (line.fieldName === 'amount') formData.value.amount = line.lineDecimal
            if (line.fieldName === 'beneficiaryMsisdn') formData.value.genericDestination = line.linePhone
            if (line.fieldName === 'description') formData.value.operationDescription = line.lineValue
        })
    })
    validateBeneficiaryName()
}

const setDataFromReuseModal = event => {
    if (event.type === 32009) {
        tabsSegmentClickHandler('naHora')
    } else if (event.type === 75 || event.type === 70004) {
        tabsSegmentClickHandler('otherbank')
    } else tabsSegmentClickHandler('samebank')

    const transferType = formTabsList.value.find(tab => tab.selected === true).id

    Object.keys(formData.value).forEach(key => {
        if (key in event) formData.value[key] = event[key]
    })

    if (!formData.value.originAccount) formData.value.originAccount = getAccountsBalances.value.find(account => account.default).id

    if (transferType === 'otherbank') {
        requestMotivesListHandler()
    } else if (transferType === 'samebank') {
        requestBeneficiaryInfoHandler()
    } else validateBeneficiaryName()
}

const setDataFromReuseBeneficiary = event => {
    Object.keys(formData.value).forEach(key => {
        if (key in event) formData.value[key] = event[key]
    })

    const transferType = formTabsList.value.find(tab => tab.selected === true).id

    if (transferType === 'naHora') validateBeneficiaryName()
    else if (transferType === 'samebank') requestBeneficiaryInfoHandler()
}

/*************************************************
*                                                *
*               Contacts Modal Logic             *
*                                                *
*************************************************/
const mobileContactSelectedHandler = contactInfo => {
    formData.value.genericDestination = contactInfo.contactData
    validateBeneficiaryName()
    showContactsModal.value = false
    triggerContactsModalClose.value = true
}

const qrCodeSuccessHandler = data => {
    formData.value.genericDestination = data
    feedbackToastData.value = {
		toastType: 'info',
		text: t('transfers.nacional.successToastQrCode')
	}
	showFeedbackToast.value = true

    validateBeneficiaryName()
}

const destroyQrCodeHandler = () => {
    /* To force QRScanner close */
    if (window.cordova && window.QRScanner) {
		window.QRScanner.destroy()
	}
}

/*************************************************
*                                                *
*                   Requests                     *
*                                                *
*************************************************/
const initialRequests = async () => {
    cancelAllPendingRequests()
    showLoader.value = true
    applyErrorState.value = false
    clearForm.value = false

    try {
        await requestTansfersCategoriesList()
        await requestAccountsBalances()
        if (getAccountsBalances.value.length) {
            /* Setting Initial Selected Account */
            formData.value.originAccount = getAccountsBalances.value.find(account => account.default).id
            /* Setting Initial Account Display Info from Default Account */
            formData.value.accountDisplayInfo = getAccountsBalances.value.find(account => account.default).accountNumber
            /* Setting Initial Currency from Default Account */
            formData.value.accountCurrency = getAccountsBalances.value.find(account => account.default).currency
        }

        if (props.favoriteInfo) setDataFromFavoritePropsParams(props.favoriteInfo)
        if (props.params) setDataFromReusePropsParams(props.params)
        if (props.dashboardCardInfo) {
            tabsSegmentClickHandler(props.dashboardCardInfo.subEntryId)

            if (getAccountsBalances.value.find(account => account.id === props.dashboardCardInfo.accountId)) {
                /* Setting Initial Selected Account */
                formData.value.originAccount = getAccountsBalances.value.find(account => account.id === props.dashboardCardInfo.accountId).id
                /* Setting Initial Account Display Info from Default Account */
                formData.value.accountDisplayInfo = getAccountsBalances.value.find(account => account.id === props.dashboardCardInfo.accountId).accountNumber
                /* Setting Initial Currency from Default Account */
                formData.value.accountCurrency = getAccountsBalances.value.find(account => account.id === props.dashboardCardInfo.accountId).currency
            }
        }

        if (isCompanySession.value) { requestRTGS() } else showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
    }
}

const requestRTGS = async () => {
    showLoader.value = true
    try {
        const res = await requestTransfersRTGS()
        rtgsResponse.value = res.data.data
        showLoader.value = false
    } catch (error) {
        showLoader.value = false
        // There is no error handling, because this doesnt affect feature to work
    }
}

const requestBeneficiaryInfoHandler = async () => {
    if (formData.value.genericDestination === '') return

    showOverlayLoader.value = true
    validatedBeneficiary.value = false
    formData.value.beneficiaryName = null

    resetBeneficiaryInfoErrorHandler()

    const payload = {
        genericDestination: formData.value.genericDestination,
        transferType: 'INTRABANK'
    }

    try {
        const res = await requestBeneficiaryDestinationInfo(payload)

        validatedBeneficiary.value = true
        formData.value.beneficiaryName = res.data.data.firstHolderName

        showOverlayLoader.value = false
    } catch (error) {
        showOverlayLoader.value = false
        if (!formData.value.sendToSameHolder) {
            beneficiaryInfoError.value = {
                errorMessage: error.response.data.message,
                errorState: true
            }
        }
    }
}

const validateBeneficiaryName = async () => {
    showOverlayLoader.value = true
    validatedBeneficiary.value = false
    formData.value.beneficiaryName = null

    resetBeneficiaryInfoErrorHandler()

    const payload = {
        operationTypeId: 32009,
		msisdn: formData.value.genericDestination
    }

    try {
        const res = await requestBeneficiaryName(payload)
        searchedBeneficiaryInfo.value = res.data.data
        formData.value.beneficiaryName = searchedBeneficiaryInfo.value.name || ''
        validatedBeneficiary.value = true
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, validateBeneficiaryName)
    }
}

const requestScheduleTypes = async () => {
    if (getScheduleTypes.value.length) return

    showOverlayLoader.value = true
    applyErrorState.value = false

    try {
        await requestTransfersScheduleTypes()

        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, requestScheduleTypes)
    }
}

const requestMotivesListHandler = async () => {
    if (!formData.value.category) return

    showOverlayLoader.value = true

    try {
        await requestTransfersMotivesList(formData.value.category)

        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestMotivesListHandler)
    }
}

const requestSelectedTransfer = async () => {
    showFeedbackToast.value = false
    if (transferRequestHappening.value) return
    transferRequestHappening.value = true
    showOverlayLoader.value = true

    const selectedTab = formTabsList.value.find(tab => tab.selected === true).id

    const payload = transferPayloadHandler()

    try {
        if (selectedTab === 'samebank') {
            frequencyTabsList.value[0].selected ? await createPontualTransfer(payload) : await createRecurringTransfer(payload)
        } else if (selectedTab === 'otherbank') {
            await createPontualTransfer(payload)
        } else await createSmartTransfer(payload)
        transferRequestHappening.value = false
        showOverlayLoader.value = false
    } catch (error) {
        transferRequestHappening.value = false
        if (error.toString() === 'Cancel') return

        let functionToUse = null
        const endDateError = error.response.data.code === 'end_date_for_recurring_transfer_is_not_a_working_day'
        const invalidDestination = error.response.data.code === 'invalid_destination_iban_same_bank'
        const insufficientFunds = error.response.data.code === 'insufficient_funds'

        if (endDateError) {
            functionToUse = endDateNotWorkingDayHandler
        } else if (invalidDestination) {
            functionToUse = invalidDestinationHandler
        } else if (insufficientFunds) {
            functionToUse = insufficientFundsHandler
        } else functionToUse = requestSelectedTransfer
        errorHandler(error.response.data, functionToUse)
    }
}

const transferPayloadHandler = () => {
    let payload
    const transferType = formTabsList.value.find(tab => tab.selected === true).id
    const transferFrequency = frequencyTabsList.value.find(tab => tab.selected === true).id

    if (transferType !== 'naHora') {
        if (transferFrequency === 'oneOffTransfer') {
            let nameOnTransfer = formData.value.beneficiaryName

            if (formData.value.sendToSameHolder && !formData.value.beneficiaryName) {
                nameOnTransfer = getAccountsBalances.value.find(account => account.accountNumber === formData.value.accountDisplayInfo).firstHolderName
            }

            payload = {
                amount: formData.value.amount,
                beneficiaryName: nameOnTransfer,
                currency: formData.value.accountCurrency,
                description: formData.value.senderDescription,
                descriptionDestination: formData.value.receiverDescription,
                genericDestination: formData.value.genericDestination.replace(/ /g, ''),
                originAccount: formData.value.originAccount,
                type: transferType === 'samebank' ? 1 : 2
            }

            if (transferType === 'otherbank') {
                payload = {
                    ...payload,
                    addressLine1: formData.value.address,
                    category: formData.value.category,
                    motive: formData.value.motive,
                    conditionsAccepted: formData.value.termsAccepted,
                    executeViaRtgs: formData.value.urgentTransfer
                }
            }
        } else {
            payload = {
                frequency: {
                    frequencyCode: formData.value.frequencyCode,
                    frequency: formData.value.frequencyCode === 'VARIAVEL' ? formData.value.frequency : null
                },
                destinationAccountNumber: formData.value.genericDestination,
                descriptionDebit: formData.value.senderDescription,
                descriptionCredit: formData.value.receiverDescription,
                originAccountId: formData.value.originAccount,
                amount: formData.value.amount,
                beneficiaryName: formData.value.beneficiaryName,
                currency: formData.value.accountCurrency,
                startDate: formData.value.startDate,
                endDate: !formData.value.endDateSelected ? null : formData.value.endDate
            }

            if (transferType === 'otherbank') {
                payload = {
                    ...payload,
                    addressLine1: formData.value.address,
                    category: formData.value.category,
                    motive: formData.value.motive,
                    conditionsAccepted: formData.value.termsAccepted,
                    executeViaRtgs: formData.value.urgentTransfer
                }
            }
        }
    } else {
        payload = {
            originAccountId: formData.value.originAccount,
            destinationMsisdn: formData.value.genericDestination,
            transferDescription: formData.value.operationDescription,
            amount: formData.value.amount,
            currency: formData.value.accountCurrency,
            beneficiaryName: formData.value.beneficiaryName
        }
    }

    return payload
}

const createBeneficiaryRequest = async temporaryName => {
    const transferType = formTabsList.value.find(tab => tab.selected === true).id

    showOverlayLoader.value = true
    preventBeneficiaryModalClose.value = true

    const payload = {
        address: null,
        email: null,
        name: temporaryName || formData.value.beneficiaryName,
        genericDestination: transferType === 'naHora' ? null : formData.value.genericDestination.replace(/ /g, ''),
        phoneNumber: transferType === 'naHora' ? formData.value.genericDestination : null
    }

    try {
        await createBeneficiary(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        preventBeneficiaryModalClose.value = false
        errorHandler(error.response.data, createBeneficiaryRequest)
    }
}

/*************************************************
*                                                *
*               Challenge Handlers               *
*                                                *
*************************************************/
const responseData = ref(null)
const feedbackOperationData = ref(null)
const isFavorite = ref(false)
const beneficiaryAdded = ref(false)

const challengeCancelHandler = () => {
    console.log('here')
}

const challengeSuccessFn = data => {
    activeStep.value !== 3 ? challengeSuccessHandler(data) : addBeneficiaryChallengeSuccessHandler(data)
}

const challengeSuccessHandler = data => {
    const transferType = formTabsList.value.find(tab => tab.selected === true).id

    if (transferType === 'naHora') {
        responseData.value = {
            ...data.data.smartTransfer,
            alreadyBeneficiary: data.data.alreadyBeneficiary,
            errorLevel: data.errorLevel,
            code: data.code
        }
    } else {
        responseData.value = {
            ...data.data,
            alreadyBeneficiary: data.data.alreadyBeneficiary,
            errorLevel: data.errorLevel,
            code: data.code
        }
    }

    isFavorite.value = data.data.favorite

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        additionalMessage: data.additionalMessage || '',
        buttonsData: data.errorLevel === 'WARNING' ? null : {
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: responseData.value.operationId },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: responseData.value.operationId } }),
            operationId: responseData.value.operationId
        }
    }

    changeStepHandler(3)
}

const addBeneficiaryChallengeSuccessHandler = data => {
    beneficiaryAdded.value = true
    preventBeneficiaryModalClose.value = false

    showOverlayLoader.value = false
    feedbackToastData.value = sucessObjectConstrutor(data.message)
    showFeedbackToast.value = true
    setTimeout(() => { showCreateBeneficiaryModal.value = false }, '200')
}

const challengeErrorFn = data => {
    activeStep.value !== 3 ? challengeErrorHandler(data) : addBeneficiaryChallengeErrorHandler(data)
}

const challengeErrorHandler = response => {
    feedbackToastData.value = errorObjectConstructor(
        response,
        requestSelectedTransfer
    )
    showFeedbackToast.value = true
    showOverlayLoader.value = false
}

const addBeneficiaryChallengeErrorHandler = response => {
    preventBeneficiaryModalClose.value = false
    errorHandler(response, createBeneficiaryRequest)
}
/*************************************************
*                                                *
*                  Error Handlers                *
*                                                *
*************************************************/
const applyErrorState = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)

	showFeedbackToast.value = true
}

const endDateNotWorkingDayHandler = () => {
    formData.value.endDate = ''
    changeStepHandler(1)
    setTimeout(() => { validateInputs() }, '200')
}

const invalidDestinationHandler = () => {
    formData.value.genericDestination = ''
    changeStepHandler(1)
    setTimeout(() => { validateInputs() }, '200')
}

const insufficientFundsHandler = () => {
    changeStepHandler(1)
}

const resetBeneficiaryInfoErrorHandler = () => {
    beneficiaryInfoError.value = {
        errorMessage: null,
        errorState: false
    }
}

const favoriteModalErrorHandler = (response) => {
    errorHandler(response)
    showFavoritesModal.value = false
}

const favoriteModalSuccessHandler = (response) => {
    isFavorite.value = !isFavorite.value
    feedbackToastData.value = sucessObjectConstrutor(response.message)
    showFeedbackToast.value = true
    showFavoritesModal.value = false
}

initialRequests()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>
